import { Component, EventEmitter, Input, isDevMode, Output } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  AdequationConfig,
  FicheConseil,
  FrequenceVersement,
  TransactionPersonnelleEnum,
  VpModeOptions,
} from '@lib/models/generated/graphql';

import { NatureOperationId } from '@lib/models/graphqlData';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiSelectOption, OuiValidationError } from 'omnium-ui/form-field';
import { config, distinctUntilChanged, filter, pairwise, skip, startWith, Subscription } from 'rxjs';
import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { FieldFormControl } from 'src/fieldFormControl/FieldFormControl';
import { FieldControlFactory } from 'src/fieldFormControl/field-control.factory';

const negativeValueError: OuiValidationError = {
  code: 'min',
  message: 'La valeur du champs ne peut être négative',
};

const requiredValueError: OuiValidationError = {
  code: 'required',
  message: 'Ce champs est requis !',
};

const maxTauxError: OuiValidationError = {
  code: 'max',
  message: 'La valeur du champs ne peut pas depasser celle par défaut',
};

const dataKeyViFrais = 'versements.versementInitial.fraisVersement';
const dataKeyViMontant = 'versements.versementInitial.montantBrut';

const dataKeyVcFrais = 'versements.versementComplementaire.fraisVersement';
const dataKeyVcMontant = 'versements.versementComplementaire.montantBrut';
const dataKeyVersementTransfert = 'versements.souscriptionAvecTransfert';

@Component({
  selector: 'app-versement',
  templateUrl: './versement.component.html',
  styleUrls: ['./versement.component.scss'],
})
export class VersementComponent extends AbstractStepComponent {
  // shouldResetFraisVersement is used to reset the value of fraisVersement only when the produit is changed
  //if the fraisversement is changed manually, shouldResetFraisVersement remain false and we keep the updated value of fraisVersement
  @Input() shouldResetFraisVersement: boolean | undefined;
  @Input() avecMiseEnPlaceOuModificationVP: FieldFormControl<boolean, boolean>;
  @Output() changementAllocationChange = new EventEmitter<boolean>();
  @Output() vpModeChange = new EventEmitter<VpModeOptions>();

  NatureOperationId = NatureOperationId;
  transfertOptions: OuiButtonToggleOption<boolean>[] = [
    {
      value: true,
      label: 'Oui',
    },
    {
      value: false,
      label: 'Non',
    },
  ];
  viFraisVersementControl: FieldFormControl<string | null, number | null>;
  viMontantBrutControl: FieldFormControl<string | null, number>;
  fraisViHint: string | undefined;
  fraisVIDefaultValue: number | undefined;
  fraisVPDefaultValue: number | undefined;
  fraisVCDefaultValue: number | undefined;
  configSub: Subscription;
  viDerogationSub: Subscription;

  slideVpControl: FieldFormControl<boolean, boolean>;
  slideTransfertControl: FieldFormControl<boolean, boolean>;
  vpFrequenceControl: FieldFormControl<FrequenceVersement | null, FrequenceVersement | null> | undefined;
  vpFrequenceOptions: OuiSelectOption<FrequenceVersement>[] = [];
  vpMontantBrutControl: FieldFormControl<string | null, number> | undefined;
  vpFraisVersementControl: FieldFormControl<string | null, number | null> | undefined;
  vpFraisVersementHint: string | undefined;
  fraisVpHint: string | undefined;

  vpFraisVersementErrors: OuiValidationError[] = [];
  vpDerogationSub: Subscription;

  slideVpSubOn: Subscription;
  slideVpSubOff: Subscription;

  vcMontantBrutControl: FieldFormControl<string | null, number> | undefined;
  vcFraisVersementControl: FieldFormControl<string | null, number | null> | undefined;
  vcFraisVersementHint: string | undefined;
  vcFraisVersementErrors: OuiValidationError[] = [];
  fraisVcHint: string | undefined;

  requiredValueError: OuiValidationError[] = [requiredValueError];
  shouldResetVP: boolean | undefined = false;
  showTransfertAllocationOptions: boolean;
  enable: boolean = false;

  isVpFrequenceEditableControl: FieldFormControl<boolean, boolean> | null;
  isVpMontantBrutEditableControl: FieldFormControl<boolean, boolean> | null;
  isVpFraisVersementEditableControl: FieldFormControl<boolean, boolean> | null;

  avecChangementAllocationControl: FieldFormControl<boolean, boolean> | null;
  avecChangementCoordonneesBancairesControl: FieldFormControl<boolean, boolean> | null;

  vpModeOptions: OuiButtonToggleOption<VpModeOptions>[] = [
    { value: VpModeOptions.MiseEnPlace, label: 'Mise en place' },
    { value: VpModeOptions.Modification, label: 'Modification' },
  ];

  vpModeControl: FieldFormControl<VpModeOptions | null>;
  VpModeOptions = VpModeOptions;
  isInitialLoad = true;
  stepTouched = false;
  isHydrating = false;

  get viMontantErrors() {
    let base = this.viMontantBrutControl?.getErrorTextfieldErrors() ?? [];
    return [...base, negativeValueError, requiredValueError];
  }

  get viTauxErrors() {
    let base = this.viFraisVersementControl?.getErrorTextfieldErrors() ?? [];
    return [...base, negativeValueError, requiredValueError];
  }

  get vpMontantErrors() {
    let base = this.vpMontantBrutControl?.getErrorTextfieldErrors() ?? [];
    return [...base, negativeValueError, requiredValueError];
  }

  get vpTauxErrors() {
    let base = this.vpFraisVersementControl?.getErrorTextfieldErrors() ?? [];
    return [...base, negativeValueError, requiredValueError];
  }

  get vcMontantErrors() {
    let base = this.vcMontantBrutControl?.getErrorTextfieldErrors() ?? [];
    return [...base, negativeValueError, requiredValueError];
  }

  get vcTauxErrors() {
    let base = this.vcFraisVersementControl?.getErrorTextfieldErrors() ?? [];
    return [...base, negativeValueError, requiredValueError];
  }

  constructor(
    protected override ficheConseilService: FicheConseilService,
    protected formConfigService: FormConfigService,
    private fieldControlFactory: FieldControlFactory
  ) {
    super(ficheConseilService);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.configSub?.unsubscribe();
    this.viDerogationSub?.unsubscribe();
    this.vpDerogationSub?.unsubscribe();
    this.slideVpSubOn?.unsubscribe();
    this.slideVpSubOff?.unsubscribe();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.slideVpControl = new FieldFormControl<boolean, boolean>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'versements.avecVersementProgramme',
      defaultValue: false,
    });

    this.initVIControls();
    this.initVCControls();

    switch (this.currentFicheConseil?.natureOperation?.id) {
      case NatureOperationId.Souscription:
        if (this.currentFicheConseil?.versements?.avecVersementProgramme) {
          this.initVPControls();
        }
        break;
      case NatureOperationId.VersementComplementaire:
        this.initVPControls();

        break;
      default:
        break;
    }

    this.configSub = this.formConfigService.currentFormConfigSubject.subscribe(config => {
      if (config) {
        this.tryGetFraisVersementInConfig(config);
        this.checkFrequenceVersementConfigAndOptions(config);
      }
    });

    this.slideTransfertControl.valueChanges
      .pipe(
        startWith(this.slideTransfertControl.value),
        pairwise(),
        filter(([prev, curr]) => !prev && curr)
      )
      .subscribe(value => {
        this.showTransfertAllocationOptions = true;
      });

    /**
     * Restore default VP fee **only** when the toggle goes OFF => ON.
     * startWith() seeds pairwise(); pairwise() gives [prev, curr];
     * filter(!prev && curr) keeps that rising edge (OFF => ON), avoiding resets on the first loading.
     */
    this.slideVpSubOn = this.slideVpControl.valueChanges
      .pipe(
        startWith(this.slideVpControl.value),
        pairwise(),
        filter(([prev, curr]) => !prev && curr)
      )
      .subscribe(value => {
        this.shouldResetVP = true;

        this.initVPControls();
      });

    /**
     * same as above, but for the OFF => ON transition.
     * We need to reset the VP controls when the toggle goes OFF => ON.
     */
    this.slideVpSubOff = this.slideVpControl.valueChanges
      .pipe(
        startWith(this.slideVpControl.value),
        pairwise(),
        filter(([prev, curr]) => prev && !curr)
      )
      .subscribe(value => {
        this.shouldResetVP = true;
        this.vpFrequenceControl?.reset();
        this.vpMontantBrutControl?.reset();
        this.vpFraisVersementControl?.reset();
      });
    if (this.currentFicheConseil?.natureOperation?.id === NatureOperationId.VersementComplementaire) {
      this.vpModeControl.valueChanges.subscribe(v => this.vpModeChange.emit(v));
    }
  }

  public override beforeFicheConseilUpdate(
    newFicheConseil: FicheConseil | null,
    previousFicheConseil: FicheConseil | null
  ): void {
    // nothing to do
    if (this.currentFicheConseil?.natureOperation?.id === NatureOperationId.VersementComplementaire) {
      const config = this.formConfigService.currentFormConfigSubject.value;
      if (config) {
        this.tryGetFraisVersementInConfig(config);
      }
    }
  }

  public override isStepValid(): boolean {
    const isSouscription = this.currentFicheConseil?.natureOperation?.id === NatureOperationId.Souscription;
    const isVersementComp = this.currentFicheConseil?.natureOperation?.id === NatureOperationId.VersementComplementaire;

    let valid = true;
    const add = (expr: boolean) => (valid = valid && expr);

    // versement initial
    if (isSouscription) {
      add(this.viMontantBrutControl.valid);
      add(this.viFraisVersementControl.valid || this.viFraisVersementControl.disabled);
    } else if (isVersementComp) {
      add(this.vcMontantBrutControl?.valid ?? false);
      add(this.vcFraisVersementControl?.valid || this.vcFraisVersementControl?.disabled || false);
    }

    // Versement Programme
    const vpBlocVisibleSousc = isSouscription && this.slideVpControl.value;
    const vpBlocVisibleVc = isVersementComp && this.avecMiseEnPlaceOuModificationVP.value;
    const vpBlocVisible = vpBlocVisibleSousc || vpBlocVisibleVc;

    if (vpBlocVisible) {
      if ((isVersementComp && this.vpModeControl?.value === VpModeOptions.MiseEnPlace) || isSouscription) {
        add(this.vpFrequenceControl?.valid ?? false);
        add(this.vpMontantBrutControl?.valid ?? false);
        add(this.vpFraisVersementControl?.valid ?? false);
      }

      if (isVersementComp && this.vpModeControl.value === VpModeOptions.Modification) {
        const hasAnyField =
          this.isVpFrequenceEditableControl?.value === true ||
          this.isVpMontantBrutEditableControl?.value === true ||
          this.isVpFraisVersementEditableControl?.value === true ||
          this.avecChangementAllocationControl?.value === true ||
          this.avecChangementCoordonneesBancairesControl?.value === true;

        add(hasAnyField);

        if (this.isVpFrequenceEditableControl?.value) {
          add(this.vpFrequenceControl?.valid ?? false);
        }
        if (this.isVpMontantBrutEditableControl?.value) {
          add(this.vpMontantBrutControl?.valid ?? false);
        }
        if (this.isVpFraisVersementEditableControl?.value) {
          add(this.vpFraisVersementControl?.valid ?? false);
        }
        if (this.avecChangementAllocationControl?.value === true) {
          add(this.avecChangementAllocationControl.valid);
        }
        if (this.avecChangementCoordonneesBancairesControl?.value === true) {
          add(this.avecChangementCoordonneesBancairesControl.valid);
        }
      }
      if (this.vpModeControl?.value) {
        add(this.vpModeControl?.valid ?? false);
      }
    }

    if (isSouscription && this.slideTransfertControl.isVisible()) {
      add(
        !this.slideTransfertControl.value ||
          (this.slideTransfertControl.value && this.slideTransfertControl?.isVisible()) // ON  => options OK
      );
    }

    return valid;
  }

  public override markAsTouched(): void {
    if (this.slideTransfertControl.isVisible()) {
      this.slideTransfertControl.markAsTouched();
    }
    this.viFraisVersementControl.markAsTouched();
    this.viMontantBrutControl.markAsTouched();
    this.vpFrequenceControl?.markAsTouched();
    this.vpFraisVersementControl?.markAsTouched();
    this.vpMontantBrutControl?.markAsTouched();
    this.stepTouched = true;
  }

  /**
   * Check if transaction personnelle is AutoSouscription,
   * if true, set fraisVersement to 0 and disable the controls.
   */
  setAutoSouscriptionFraisVersement(control: FieldFormControl<string | null, number | null>) {
    const isAutoSouscription =
      this.currentFicheConseil?.transactionPersonnelle === TransactionPersonnelleEnum.AutoSouscription;
    if (isAutoSouscription) {
      control.setValue('0');
      control.updateValueAndValidity();
      control.disable();
    }
    return isAutoSouscription;
  }

  initVIControls() {
    this.slideTransfertControl = this.fieldControlFactory.makeBoolCtrl(dataKeyVersementTransfert, false);

    this.viMontantBrutControl = this.fieldControlFactory.makeAmountStringCtrl(dataKeyViMontant, null, [
      Validators.required,
      Validators.min(0),
    ]);

    this.viFraisVersementControl = this.fieldControlFactory.makeAmountStringCtrl(dataKeyViFrais, '0', [
      Validators.required,
      Validators.min(0),
    ]);

    if (!this.setAutoSouscriptionFraisVersement(this.viFraisVersementControl)) {
      this.setDefaultFraisVersement(this.fraisVIDefaultValue, this.viFraisVersementControl);
    }
  }
  initVCControls() {
    this.vcMontantBrutControl = this.fieldControlFactory.makeAmountStringCtrl(dataKeyVcMontant, null, [
      Validators.required,
      Validators.min(0),
    ]);

    this.vcFraisVersementControl = this.fieldControlFactory.makeAmountStringCtrl(dataKeyVcFrais, '0', [
      Validators.required,
      Validators.min(0),
    ]);
  }
  initVPControls() {
    this.vpFrequenceControl = this.fieldControlFactory.makeCtrl<FrequenceVersement | null, FrequenceVersement | null>(
      'versements.versementProgramme.frequence',
      FrequenceVersement.Mensuel,
      { validators: [Validators.required] }
    );

    this.vpMontantBrutControl = this.fieldControlFactory.makeAmountStringCtrl(
      'versements.versementProgramme.versement.montantBrut',
      null,
      [Validators.required, Validators.min(0)]
    );

    this.vpFraisVersementControl = this.fieldControlFactory.makeAmountStringCtrl(
      'versements.versementProgramme.versement.fraisVersement',
      '0',
      [Validators.required, Validators.min(0)]
    );

    const isVC = this.currentFicheConseil?.natureOperation?.id === NatureOperationId.VersementComplementaire;

    if (!this.setAutoSouscriptionFraisVersement(this.vpFraisVersementControl) && isVC === false) {
      this.setDefaultFraisVersement(this.fraisVPDefaultValue, this.vpFraisVersementControl);
    }

    if (isVC) {
      this.vpModeControl = new FieldFormControl<VpModeOptions | null>({
        formConfigService: this.formConfigService,
        ficheConseilService: this.ficheConseilService,
        dataKey: 'vpModeOptions',
        defaultValue: VpModeOptions.MiseEnPlace,
        options: {
          validators: [Validators.required],
        },
      });

      if (this.avecMiseEnPlaceOuModificationVP?.value === true && !this.vpModeControl.value) {
        this.vpModeControl.setValue(VpModeOptions.MiseEnPlace);
      }

      this.isVpFrequenceEditableControl = this.fieldControlFactory.makeBoolCtrl(
        'versements.versementProgramme.isFrequenceEditable',
        true
      );
      this.isVpMontantBrutEditableControl = this.fieldControlFactory.makeBoolCtrl(
        'versements.versementProgramme.isMontantBrutEditable',
        true
      );
      this.isVpFraisVersementEditableControl = this.fieldControlFactory.makeBoolCtrl(
        'versements.versementProgramme.isFraisVersementEditable',
        true
      );
      this.avecChangementAllocationControl = this.fieldControlFactory.makeBoolCtrl(
        'versements.versementProgramme.avecChangementAllocation',
        false
      );
      this.avecChangementCoordonneesBancairesControl = this.fieldControlFactory.makeBoolCtrl(
        'versements.versementProgramme.avecChangementCoordonneesBancaires',
        false
      );

      this.avecMiseEnPlaceOuModificationVP?.valueChanges.pipe(distinctUntilChanged(), skip(1)).subscribe(value => {
        this.setFraisVersementVPDefaultValue(); //FIXME : we should already have this value from the config service

        if (!value) {
          this.vpModeControl?.setValue(null);
          this.vpFrequenceControl?.reset();
          this.vpMontantBrutControl?.setValue(null);
          this.avecChangementAllocationControl?.reset();
          this.avecChangementCoordonneesBancairesControl?.reset();
        } else this.vpModeControl?.setValue(VpModeOptions.MiseEnPlace);
      });

      this.vpModeControl?.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
          return; // Skip the first change event
        }
        this.setFraisVersementVPDefaultValue(); //FIXME : we should already have this value from the config service

        this.vpFrequenceControl?.setValue(null);
        this.vpMontantBrutControl?.setValue(null);
        this.vpFraisVersementControl?.setValue(this.fraisVPDefaultValue?.toString() ?? null);

        this.avecChangementAllocationControl?.reset();
        this.avecChangementCoordonneesBancairesControl?.setValue(false);

        if (value === VpModeOptions.Modification) {
          this.isVpFrequenceEditableControl?.setValue(false);
          this.isVpMontantBrutEditableControl?.setValue(false);
          this.isVpFraisVersementEditableControl?.setValue(false);
          this.avecChangementAllocationControl?.setValue(false);
          this.vpFrequenceControl?.disable();
          this.vpMontantBrutControl?.disable();
          this.vpFraisVersementControl?.disable();
        } else if (value === VpModeOptions.MiseEnPlace) {
          this.vpFrequenceControl?.enable();
          this.vpMontantBrutControl?.enable();
          this.vpFraisVersementControl?.enable();
        }
      });

      this.isVpFrequenceEditableControl?.valueChanges.subscribe(value => {
        if (value) {
          this.vpFrequenceControl?.enable();
        } else if (this.vpModeControl?.value === VpModeOptions.Modification) {
          this.vpFrequenceControl?.disable();
          this.vpFrequenceControl?.setValue(null);
        }
      });

      this.isVpMontantBrutEditableControl?.valueChanges.subscribe(value => {
        if (value) {
          this.vpMontantBrutControl?.enable();
        } else if (this.vpModeControl?.value === VpModeOptions.Modification) {
          this.vpMontantBrutControl?.disable();
          this.vpMontantBrutControl?.setValue(null);
        }
      });
      this.isVpFraisVersementEditableControl?.valueChanges.subscribe(value => {
        if (value) {
          this.vpFraisVersementControl?.enable();
        } else if (this.vpModeControl?.value === VpModeOptions.Modification) {
          this.vpFraisVersementControl?.disable();
          this.vpFraisVersementControl?.reset();
        }
      });
    }
  }

  private setFraisVersementVPDefaultValue() {
    const fraisVersementVPValue = this.getFraisVersementByFieldKey(
      'versements.versementProgramme.versement.fraisVersement',
      this.formConfigService.currentFormConfigSubject.value!
    );
    this.vpFraisVersementControl?.setValue(fraisVersementVPValue?.toString() ?? null);
  }

  private checkFrequenceVersementConfigAndOptions(config: AdequationConfig) {
    const configFrequenceImpossible = config.fieldConfigs
      ?.find(config => config.fieldKey === 'versements.versementProgramme.frequence')
      ?.validators?.find(validator => validator.validatorKey === 'frequenceImpossible')
      ?.parameters?.find(parameter => parameter.key === 'liste')?.value;

    if (!configFrequenceImpossible) {
      this.vpFrequenceOptions = this.buildAndCheckFrequenceOptions();
      return;
    }
    const impossibleListe = configFrequenceImpossible.split(',');
    this.vpFrequenceOptions = this.buildAndCheckFrequenceOptions(impossibleListe);
  }

  private tryGetFraisVersementInConfig(config: AdequationConfig) {
    if (this.currentFicheConseil?.natureOperation?.id === NatureOperationId.Souscription) {
      const fraisVersementVIValue = this.getFraisVersementByFieldKey(
        'versements.versementInitial.fraisVersement',
        config
      );
      if (fraisVersementVIValue && this.viFraisVersementControl?.enabled) {
        this.fraisViHint = `Par défaut ${fraisVersementVIValue}%`;
        this.fraisVIDefaultValue = Number(fraisVersementVIValue);
        this.setDefaultFraisVersement(fraisVersementVIValue, this.viFraisVersementControl);
      }
    }

    const fraisVersementVPValue = this.getFraisVersementByFieldKey(
      'versements.versementProgramme.versement.fraisVersement',
      config
    );
    if (fraisVersementVPValue) {
      this.fraisVpHint = `Par défaut ${fraisVersementVPValue}%`;
      this.fraisVPDefaultValue = Number(fraisVersementVPValue);

      if (this.vpFraisVersementControl?.enabled && this.vpFraisVersementControl?.value === null) {
        this.setDefaultFraisVersement(fraisVersementVPValue, this.vpFraisVersementControl);
      }
    }

    const fraisVersementVCValue = this.getFraisVersementByFieldKey(dataKeyVcFrais, config);
    if (fraisVersementVCValue) {
      this.fraisVcHint = `Par défaut ${fraisVersementVCValue}%`;
      this.fraisVCDefaultValue = Number(fraisVersementVCValue);

      if (
        this.vcFraisVersementControl?.enabled &&
        this.vcFraisVersementControl?.value === null &&
        this.currentFicheConseil
      ) {
        this.setDefaultFraisVersement(fraisVersementVCValue, this.vcFraisVersementControl);
      }
    }
    this.shouldResetFraisVersement = false;
  }

  private setDefaultFraisVersement(defaultValue: any, control: any) {
    if (this.currentFicheConseil?.natureOperation?.id === NatureOperationId.Souscription) {
      const mustReset = this.shouldResetFraisVersement || this.shouldResetVP;
      if (!mustReset) return;

      if (!defaultValue) return;
      control.setValue(defaultValue);
      this.shouldResetVP = false;
    } else {
      control.setValue(defaultValue);
    }
  }
  getFraisVersementByFieldKey(fieldKey: string, config: AdequationConfig): string | undefined {
    return config.fieldConfigs
      ?.find(config => config.fieldKey === fieldKey)
      ?.validators?.find(validator => validator.validatorKey === 'fraisVersement')
      ?.parameters?.find(parameter => parameter.key === 'tauxParDefaut')?.value;
  }

  buildAndCheckFrequenceOptions(forbiddenList: string[] = []): OuiSelectOption<FrequenceVersement>[] {
    this.checkResetFrequenceVersement(forbiddenList);
    let order = ['Mensuel', 'Trimestriel', 'Semestriel', 'Annuel'];
    if (forbiddenList.length) {
      order = order.filter(key => !forbiddenList.includes(key));
    }
    const frequenceVersementOptions: OuiSelectOption<FrequenceVersement>[] = order.map(key => ({
      label: key,
      value: FrequenceVersement[key as keyof typeof FrequenceVersement],
    }));

    return frequenceVersementOptions;
  }

  checkResetFrequenceVersement(forbiddenList: string[] = []) {
    const versementFrequence = this.currentFicheConseil?.versements?.versementProgramme?.frequence;
    if (!versementFrequence || forbiddenList.length === 0) {
      return;
    }
    const frequenceString = versementFrequence.toLocaleLowerCase();
    const isForbidden = forbiddenList.some(item => item.toLowerCase() === frequenceString);
    if (isForbidden) {
      this.ficheConseilService.onFicheConseilDataEdit('versements.versementProgramme.frequence', null);
      this.vpFrequenceControl?.reset();
    }
  }

  /**
   * Handles the input event for a text field to ensure that the input value
   * contains no more than two decimal places. If the input value has more
   * than two decimal places, it truncates the value to two decimals.
   *
   * @param event - The input event triggered by the text field.
   */

  onMontantInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (value.includes('.')) {
      const [integerPart, decimalPart] = value.split('.');
      if (decimalPart.length > 2) {
        // Limite la partie décimale à deux chiffres
        value = `${integerPart}.${decimalPart.slice(0, 2)}`;
        input.value = value;
      }
    }
  }

  onChangementAllocationToggle(checked: boolean): void {
    this.changementAllocationChange.emit(checked);
  }
}
